// src/pages/VerifyEmail.tsx - Fixed with mobile-responsive OTP
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { verifyEmail, getProfile, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Mail,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { authService } from "../services/api";

const VerifyEmail: React.FC = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const { isLoading, error, user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  // Get email from location state or user object
  const email = location.state?.email || user?.email;

  useEffect(() => {
    if (!email) {
      navigate("/register");
    }
  }, [email, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (isAuthenticated && user?.email_verified_at) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, user, navigate]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Please enter all 6 digits");
      return;
    }

    // Check if all digits are numbers
    if (!/^\d{6}$/.test(otpString)) {
      toast.error("Please enter valid digits (0-9)");
      return;
    }

    try {
      const result = await dispatch(verifyEmail(otpString) as any);

      if (verifyEmail.fulfilled.match(result)) {
        toast.success("Email verified successfully!");
        dispatch(getProfile() as any);
        navigate("/dashboard");
      }
    } catch (error: any) {
      // Don't show "session expired" for verification errors
      if (!error?.includes("session expired")) {
        toast.error(error || "Verification failed");
      }
    }
  };

  const handleResend = async () => {
    if (countdown > 0) {
      toast.error(
        `Please wait ${countdown} seconds before requesting a new code`
      );
      return;
    }

    setIsResending(true);
    try {
      await authService.resendVerification(email!);
      toast.success("Verification code sent successfully!");
      setCountdown(60); // 60 seconds cooldown
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Failed to resend verification code"
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        // Clear the previous input
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      const newOtp = [...otp];

      digits.slice(0, 6).forEach((digit, index) => {
        newOtp[index] = digit;
      });

      setOtp(newOtp);

      // Focus the last input
      const lastInput = inputRefs.current[Math.min(5, digits.length - 1)];
      if (lastInput) lastInput.focus();
    } else {
      toast.error("Please paste exactly 6 digits");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </div>

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Verify Your Email
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            We sent a 6-digit code to{" "}
            <span className="font-medium text-primary">{email}</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            The code expires in 10 minutes
          </p>
        </div>

        {/* Verification Form */}
        <div className={GlobalStyles.card}>
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter the verification code sent to your email address
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* OTP Inputs - Fixed for mobile */}
            <div className="space-y-4">
              <label className={`${GlobalStyles.inputLabel} text-center block`}>
                Verification Code
              </label>
              <div
                className="flex justify-center space-x-2 sm:space-x-3"
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`${GlobalStyles.input} w-10 h-12 sm:w-12 text-center text-xl font-semibold !p-0 text-gray-900 dark:text-white`}
                    autoFocus={index === 0}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    style={{
                      fontSize: "1.25rem",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                You can paste the 6-digit code
              </p>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || otp.join("").length !== 6}
                className={`${GlobalStyles.button} ${
                  GlobalStyles.buttonPrimary
                } ${GlobalStyles.buttonLarge} w-full ${
                  otp.join("").length !== 6
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                    ></div>
                    Verifying...
                  </div>
                ) : (
                  "Verify Email"
                )}
              </button>
            </div>
          </form>

          {/* Resend Code */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResend}
              disabled={isResending || countdown > 0}
              className={`${GlobalStyles.button} ${
                GlobalStyles.buttonOutline
              } ${GlobalStyles.buttonSmall} inline-flex items-center ${
                countdown > 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isResending ? (
                <>
                  <div className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}></div>
                  Sending...
                </>
              ) : countdown > 0 ? (
                `Resend in ${countdown}s`
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend Code
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-6`}>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <CheckCircle className="h-5 w-5" />
            </div>
            <div className="text-sm">
              <p className="font-medium mb-1">Verification Tips:</p>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Check your spam folder if you don't see the email</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>The code expires in 10 minutes</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>You can request a new code if it expires</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Make sure you entered the correct email address</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div
          className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} mt-4`}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="text-sm">
              <p className="font-medium mb-1">Important:</p>
              <p className="text-gray-600 dark:text-gray-400">
                Email verification is required to access all platform features
                and ensure account security.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
